import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import { setBookSession } from "app/actions/scheduling/bookSession";
import { RootState } from "typedefs";
import styles from "./styles.module.scss";

import Navbar from "app/components/Navbar";
import OnlinePayment from "app/images/online-payment.svg";
import OfflinePayment from "app/images/offline-payment.svg";
import CheckMark from "app/images/checkmark.svg";
import { CreditBundle, GroupedByProduct } from "app/models/scheduling/CreditBundleModel";
import { groupByProduct, sortBundlesExpiringFirst } from "app/utils/creditBundles";

import UserAvatar from "app/components/Programs/UserAvatar";
import UserItems from "app/components/Programs/UserItems";

import { DateTime } from "luxon";
import { formatMonthDayWithYearIfNeeded } from "app/utils/luxonFormats";
import { ageFromBirthdate } from "app/utils/age";
import {
  schedulingFetchGet,
  schedulingFetchPost,
} from "app/utils/request/scheduling/fetch";
import { StripePurchaseModel, CheckoutDisplayInfo, isProgramRegistration } from "app/models/scheduling/StripePurchaseModel";
import { StudentProgramRegistrationModel } from "app/models/scheduling/StudentProgramRegistrationModel";
import { StudentProfileModel } from "app/models/scheduling/StudentProfileModel";
import SuccessRegistrationModal from "app/components/Programs/SuccessRegistrationModal";
import WaitlistConfirmationModal from "app/components/Programs/WaitlistConfirmationModal";
import CancelPurchaseConfirmationModal from "app/components/CancelPurchaseConfirmationModal";
import CheckoutErrorModal from "app/components/CheckoutErrorModal";
import TimezoneInfo from "app/components/TimezoneInfo";
import { Loading } from "app/components/Wrappers";
import {Trans, useTranslation} from "react-i18next";
import { generateCurrencySymbol } from "app/routes/BuildProgram/Pricing/utils";
import { useBookingParticipants } from "app/routes/Sessions/useBookingParticipants";
import { ResourceLine } from "app/utils/request/scheduling/facilityResources";

type PaymentMethodType = "online" | "offline" | "credit_bundle";

// The bay the hold took, after the Location row with the other icon rows:
// one row a section, the section title as the label and the bay titles as
// the value ("Bays | Bay 1"). A component, so the big checkout component gets
// no new branch (the hooks lint rule counts them).
function ResourceRows({ lines }: { lines?: ResourceLine[] }) {
  return (
    <>
      {(lines ?? []).map((line) => (
        <div className={styles.specRow} data-testid="checkout-resource-row" key={line.section}>
          <div className={styles.leftWithIcon}>
            <span className={styles.iconBubble}>
              <i className="ico ico-map-marker"></i>
            </span>
            <span>{line.section}</span>
          </div>
          <div className={styles.valueStrong}>{line.titles}</div>
        </div>
      ))}
    </>
  );
}

export default function PurchaseCheckout() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { id } = useParams<{ id: string }>();

  // Where "back" goes. Each purchase type has a sensible default, but a screen
  // that sends the athlete here can name the page it wants them returned to —
  // the booking screen they were on, on the tab they were on — with
  // ?return_url=. Relative paths only: this is a redirect target.
  const returnUrl = useMemo(() => {
    const raw = new URLSearchParams(location.search).get("return_url");
    return raw && raw.startsWith("/") && !raw.startsWith("//") ? raw : null;
  }, [location.search]);
  const { self: currentUser } = useSelector((state: RootState) => state.user, shallowEqual);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [purchase, setPurchase] = useState<StripePurchaseModel | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodType>("online");
  const [submitting, setSubmitting] = useState(false);
  const [notes, setNotes] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState("");
  const [errorModalTitle, setErrorModalTitle] = useState<string | undefined>(undefined);
  const [showNoMethodsModal, setShowNoMethodsModal] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [countdown, setCountdown] = useState("");
  const { t } = useTranslation();

  // Credit bundle state
  const [creditBundles, setCreditBundles] = useState<CreditBundle[]>([]);
  const [groupedBundles, setGroupedBundles] = useState<GroupedByProduct[]>([]);
  const [selectedBundleId, setSelectedBundleId] = useState<number | null>(null);
  const [bundlesLoading, setBundlesLoading] = useState(false);
  const [bundlesFetched, setBundlesFetched] = useState(false);

  // Program data (fetched separately since backend doesn't include details in purchase response)
  const [programData, setProgramData] = useState<any>(null);

  // Get displayInfo from purchase (via first purchase item)
  const displayInfo: CheckoutDisplayInfo | undefined = purchase?.getDisplayInfo();

  // CN-1323: when a family head booked on behalf of a child, the purchase
  // carries the dependent. Enrich it with the head's roster (same source as the
  // booking picker) for the relationship + age label; fall back to the
  // purchase-supplied name/birthdate so the row still renders without the roster.
  const { familyMembers } = useBookingParticipants();
  const participantDisplay = useMemo(() => {
    const participant = displayInfo?.participant;
    if (!participant) {
      return null;
    }

    const member = familyMembers.find((m) => m.userId === participant.externalId);
    const firstName = member?.firstName ?? participant.firstName;
    const lastName = member?.lastName ?? participant.lastName;
    const name =
      `${firstName ?? ""} ${lastName ?? ""}`.trim() || participant.fullName || "";

    const age = member?.age ?? ageFromBirthdate(participant.birthdate);
    const subLabel = age != null ? t("Age {{age}}", { age }) : "";

    return {
      avatar: member?.avatar ?? participant.profilePicture,
      firstName,
      lastName,
      name,
      subLabel,
    };
  }, [displayInfo?.participant, familyMembers, t]);

  // Check if Stripe integration is enabled for the collecting coach
  const stripeEnabled = !!purchase?.stripeIntegrationEnabled;

  // Load purchase data on mount
  useEffect(() => {
    const loadPurchase = async () => {
      setLoading(true);
      setError(null);

      try {
        // Fetch purchase data (includes displayInfo from API)
        const purchaseResponse = await schedulingFetchGet(`/stripe_purchases/${id}`);
        const purchaseModel = new StripePurchaseModel(purchaseResponse);
        setPurchase(purchaseModel);

        // If user already started Stripe checkout, redirect them back to it
        if (purchaseModel.checkoutUrl) {
          window.location.href = purchaseModel.checkoutUrl;
          return;
        }

        // Check if we have displayInfo (from purchase items)
        const info = purchaseModel.getDisplayInfo();
        if (!info) {
          setError("Unable to load purchase details.");
          setLoading(false);
          return;
        }

        // Set default payment method based on available_payment_methods or collectType
        const hasStripe = !!purchaseModel.stripeIntegrationEnabled;
        const firstItem = purchaseModel.purchaseItems?.[0];
        const rawDetails = (firstItem as any)?.details;
        const availableMethods: string[] | undefined = rawDetails?.availablePaymentMethods;

        if (availableMethods && Array.isArray(availableMethods) && availableMethods.length > 0) {
          // Session bookings: prefer Package/Subscription when available
          if (info.type === 'lesson' && availableMethods.includes('package')) {
            setSelectedPaymentMethod("credit_bundle");
          } else {
            // Coach Default: use onlineDefault when Stripe is enabled, otherwise offline
            const coachDefault: PaymentMethodType =
              (hasStripe && info.onlineDefault) ? "online" : "offline";
            setSelectedPaymentMethod(
              availableMethods.includes(coachDefault) ? coachDefault : "offline"
            );
          }
        } else {
          // Fallback: use old collectType logic
          if (info.collectType === "online" && hasStripe) {
            setSelectedPaymentMethod("online");
          } else if (info.collectType === "offline" || !hasStripe) {
            setSelectedPaymentMethod("offline");
          } else if (info.collectType === "both") {
            setSelectedPaymentMethod(hasStripe && info.onlineDefault ? "online" : "offline");
          }
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading purchase:", err);
        setError("Unable to load purchase details. The purchase may have expired.");
        setLoading(false);
      }
    };

    loadPurchase();
  }, [id]);

  // Fetch credit bundles for lesson bookings
  useEffect(() => {
    if (displayInfo?.type === "lesson" && displayInfo.lessonRateId && displayInfo.coachId) {
      setBundlesLoading(true);
      // CN-1323 (CFTB-3304): a credit bundle is owned by the consumer, so when
      // booking for a dependent, scope to that participant's bundles (the head
      // is authorized to view them); otherwise fall back to the buyer's own.
      const participantExternalId = displayInfo.participant?.externalId;
      schedulingFetchGet(
        "/credit_bundles",
        participantExternalId ? { student_id: participantExternalId } : {},
      )
        .then((bundles: CreditBundle[]) => {
          // Filter: same coach OR same facility, has sessions, matches lesson rate
          // CN-2641: sorted expiring-first, so a group's first bundle id (the
          // one sent as credit_bundle_id) is the soonest-expiring one.
          // The expiry check is defensive — /credit_bundles already applies
          // CreditBundle.valid, so an expired bundle never arrives — but this page
          // fetches once on mount, and a bundle can lapse while it sits open. The
          // BE re-resolves the id through .valid and fails the checkout, so
          // dropping it here means the option is never offered rather than
          // erroring on submit. Same guard getPackageCreditsByLessonRate applies.
          const now = new Date();
          const eligible = sortBundlesExpiringFirst(bundles.filter(
            (b) =>
              (b.coachProfile?.id === displayInfo.coachId ||
               (b.owner?.type === "Facility" && b.owner?.id === displayInfo.facilityId)) &&
              (b.remainingSessions > 0 || b.unlimited || b.remainingSessions === null) &&
              b.lessonRate !== null &&
              b.lessonRate.id === displayInfo.lessonRateId &&
              !(b.expiredAt && new Date(b.expiredAt) < now)
          ));

          // Group by product
          const grouped = groupByProduct(eligible);

          setCreditBundles(eligible);
          setGroupedBundles(grouped);
        })
        .catch(() => setCreditBundles([]))
        .finally(() => {
          setBundlesLoading(false);
          setBundlesFetched(true);
        });
    }
  }, [
    displayInfo?.type,
    displayInfo?.lessonRateId,
    displayInfo?.coachId,
    displayInfo?.participant?.externalId,
  ]);

  // Fetch program data (not included in purchase response)
  useEffect(() => {
    if (displayInfo?.type === "program" && purchase?.programIds?.[0]) {
      schedulingFetchGet(`/programs/${purchase.programIds[0]}`)
        .then((program: any) => {
          setProgramData(program);

          // Skip payment method update if available_payment_methods is present (new API)
          const firstItem = purchase.purchaseItems?.[0];
          const rawDetails = (firstItem as any)?.details;
          const availableMethods: string[] | undefined = rawDetails?.availablePaymentMethods;
          if (availableMethods && Array.isArray(availableMethods) && availableMethods.length > 0) {
            return; // Payment method already set from available_payment_methods
          }

          // Fallback: Update selected payment method based on program's collectType and Stripe integration
          const hasStripe = !!purchase.stripeIntegrationEnabled;
          if (program.collectType === "online" && hasStripe) {
            setSelectedPaymentMethod("online");
          } else if (program.collectType === "offline" || !hasStripe) {
            setSelectedPaymentMethod("offline");
          } else if (program.collectType === "both") {
            setSelectedPaymentMethod(hasStripe && program.onlineDefault ? "online" : "offline");
          }
        })
        .catch(() => {
          // Keep default from displayInfo if fetch fails
        });
    }
  }, [displayInfo?.type, purchase?.programIds]);

  // Determine which payment methods to show
  // New logic: read available_payment_methods from purchase item details
  // Fallback to old collectType logic if not present
  const { showOnline, showOffline, showPackage } = useMemo(() => {
    // Try to get available_payment_methods from the first purchase item
    const firstItem = purchase?.purchaseItems?.[0];
    const rawDetails = (firstItem as any)?.details;
    const availableMethods: string[] | undefined = rawDetails?.availablePaymentMethods;

    if (availableMethods && Array.isArray(availableMethods) && availableMethods.length > 0) {
      // New logic: use available_payment_methods array
      return {
        showOnline: availableMethods.includes('online'),
        showOffline: availableMethods.includes('offline'),
        showPackage: availableMethods.includes('package'),
      };
    }

    // Fallback: use old collectType logic for backward compatibility
    const effectiveCollectType = displayInfo?.type === "program" && programData?.collectType
      ? programData.collectType
      : displayInfo?.collectType;

    const showOnlineOnly = stripeEnabled && effectiveCollectType === "online";
    const showOfflineOnly = effectiveCollectType === "offline" || !stripeEnabled;
    const showBoth = stripeEnabled && effectiveCollectType === "both";

    return {
      showOnline: showOnlineOnly || showBoth,
      showOffline: showOfflineOnly || showBoth,
      showPackage: displayInfo?.type === "lesson", // Packages only available for lessons in legacy mode
    };
  }, [purchase?.purchaseItems, displayInfo?.type, displayInfo?.collectType, programData?.collectType, stripeEnabled]);

  // Package-only rate but student has no matching packages
  const packageRequired = showPackage && !showOnline && !showOffline && groupedBundles.length === 0 && !bundlesLoading;


  // Default to credit_bundle if eligible bundles exist and package option is available
  useEffect(() => {
    if (creditBundles.length > 0 && !bundlesLoading && showPackage) {
      setSelectedPaymentMethod("credit_bundle");
      setSelectedBundleId(creditBundles[0].id);
    }
  }, [creditBundles, bundlesLoading, showPackage]);

  // Clear selectedBundleId when switching away from credit_bundle
  useEffect(() => {
    if (selectedPaymentMethod !== "credit_bundle" && selectedBundleId !== null) {
      setSelectedBundleId(null);
    }
  }, [selectedPaymentMethod, selectedBundleId]);

  // Safety check: Force valid payment method if selected method is not available
  useEffect(() => {
    // If online is selected but not available, switch to first available
    if (selectedPaymentMethod === "online" && !showOnline) {
      if (showOffline) {
        setSelectedPaymentMethod("offline");
      } else if (showPackage && groupedBundles.length > 0) {
        setSelectedPaymentMethod("credit_bundle");
        setSelectedBundleId(groupedBundles[0].creditBundleIds[0]);
      }
    }
    // If offline is selected but not available, switch to first available
    if (selectedPaymentMethod === "offline" && !showOffline) {
      if (showOnline) {
        setSelectedPaymentMethod("online");
      } else if (showPackage && groupedBundles.length > 0) {
        setSelectedPaymentMethod("credit_bundle");
        setSelectedBundleId(groupedBundles[0].creditBundleIds[0]);
      }
    }
    // If credit_bundle is selected but not available, switch to first available
    if (selectedPaymentMethod === "credit_bundle" && (!showPackage || groupedBundles.length === 0)) {
      if (showOnline) {
        setSelectedPaymentMethod("online");
      } else if (showOffline) {
        setSelectedPaymentMethod("offline");
      }
    }
  }, [showOnline, showOffline, showPackage, selectedPaymentMethod, groupedBundles]);

  // Countdown timer for reservation expiration
  useEffect(() => {
    if (!purchase?.expiresAt) {
      return undefined;
    }

    const updateCountdown = () => {
      const now = new Date();
      const target = new Date(purchase.expiresAt);
      const diff = target.getTime() - now.getTime();

      if (diff <= 0) {
        setCountdown("");
        setIsExpired(true);
        return;
      }

      const mins = Math.floor(diff / 60000);
      const secs = Math.floor((diff % 60000) / 1000);
      setCountdown(`${mins}:${secs.toString().padStart(2, "0")}`);
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [purchase?.expiresAt]);

  // Split purchase items into registered vs waitlisted (only for programs)
  const { registrationCandidates, waitlistCandidates } = useMemo(() => {
    if (!purchase || displayInfo?.type !== "program") {
      return { registrationCandidates: [], waitlistCandidates: [] };
    }

    const mapToRegistrationModel = (item: any) => {
      if (!isProgramRegistration(item)) {
        return null;
      }
      const studentData = item.studentProfile;
      if (!studentData) {
        return null;
      }

      const profile = new StudentProfileModel({
        id: studentData.id || `${item.id}`,
        firstName: studentData.firstName,
        lastName: studentData.lastName,
        email: studentData.email,
        profilePicture: studentData.profilePicture || "",
        // CN-1323 (CFTB-3292): a managed dependent shows their age, not the
        // (dead/temp) email — UserItem keys off these.
        birthdate: studentData.birthdate ?? null,
        headExternalId: studentData.headExternalId ?? null,
      });

      return new StudentProgramRegistrationModel(
        {
          id: item.id,
          status: item.registrationStatus || "pending",
          createdAt: new Date().toISOString(),
          canRemove: false,
          paymentMethod: "offline",
        },
        profile
      );
    };

    return {
      registrationCandidates: purchase.pendingItems.map(mapToRegistrationModel).filter(Boolean) as StudentProgramRegistrationModel[],
      waitlistCandidates: purchase.waitlistedItems.map(mapToRegistrationModel).filter(Boolean) as StudentProgramRegistrationModel[],
    };
  }, [purchase, displayInfo?.type]);

  // Check if all candidates are waitlisted (no charge needed) - only for programs
  const allWaitlisted = displayInfo?.type === "program" && registrationCandidates.length === 0 && waitlistCandidates.length > 0;

  // Effective display values - use programData for programs, displayInfo for lessons
  const effectiveTitle = displayInfo?.type === "program" && programData?.title
    ? programData.title
    : displayInfo?.title;

  const effectiveLocation = displayInfo?.type === "program" && programData?.location
    ? programData.location
    : displayInfo?.location;

  const effectiveFacilityName = displayInfo?.type === "program" && programData?.facilityName
    ? programData.facilityName
    : displayInfo?.facilityName;

  const effectiveTimeZone = displayInfo?.type === "program" && programData?.timeZone
    ? programData.timeZone
    : displayInfo?.timeZone || "UTC";

  const effectiveCoaches = useMemo(() => {
    if (displayInfo?.type === "program" && programData?.coaches?.length > 0) {
      return programData.coaches.map((c: any) => ({
        firstName: c.firstName,
        lastName: c.lastName,
        profilePicture: c.profilePicture,
        externalId: c.id,
      }));
    }
    return displayInfo?.coaches || [];
  }, [displayInfo?.type, displayInfo?.coaches, programData?.coaches]);

  // startTime/endTime span the whole recurrence group, so a drop-in clinic
  // date would show the group's first session. Its own start/end is the
  // date being registered for (CN-2867).
  const isDropIn = programData?.attendance === "clinic";
  const programStartTime = isDropIn ? programData?.start : programData?.startTime;
  const programEndTime = isDropIn ? programData?.end : programData?.endTime;

  // Format date from displayInfo or programData
  const dateLabel = useMemo(() => {
    const dateSource = displayInfo?.type === "program" && programStartTime
      ? programStartTime
      : displayInfo?.date;
    if (!dateSource) {
      return "—";
    }
    return DateTime.fromISO(dateSource, { zone: effectiveTimeZone }).toFormat("LLLL d, yyyy");
  }, [displayInfo?.type, displayInfo?.date, programStartTime, effectiveTimeZone]);

  // Format time from displayInfo or programData
  const timeLabel = useMemo(() => {
    const startTime = displayInfo?.type === "program" && programStartTime
      ? programStartTime
      : displayInfo?.startTime;
    const endTime = displayInfo?.type === "program" && programEndTime
      ? programEndTime
      : displayInfo?.endTime;

    if (!startTime || !endTime) {
      return "—";
    }

    const s = DateTime.fromISO(startTime, { zone: effectiveTimeZone }).toFormat("h:mm a");
    const e = DateTime.fromISO(endTime, { zone: effectiveTimeZone }).toFormat("h:mm a");
    return `${s} - ${e}`;
  }, [displayInfo?.type, displayInfo?.startTime, displayInfo?.endTime, programStartTime, programEndTime, effectiveTimeZone]);

  // Format total
  const totalDisplay = useMemo(() => {
    if (!purchase || !displayInfo) {
      return "—";
    }
    const symbol = generateCurrencySymbol(purchase.currency);
    const currencyCode = (purchase.currency || "").toUpperCase();
    return `${purchase.getTotalFormatted(symbol)} ${currencyCode}`;
  }, [purchase, displayInfo]);

  // Format price per item
  const priceDisplay = useMemo(() => {
    if (!purchase || !displayInfo) {
      return "—";
    }
    const symbol = generateCurrencySymbol(purchase.currency);
    const currencyCode = (purchase.currency || "").toUpperCase();
    const price = displayInfo.unitPrice || 0;
    return `${symbol}${price.toFixed(2)} ${currencyCode}`;
  }, [displayInfo, purchase]);

  // Handle cancel - shows confirmation modal
  const handleCancel = () => {
    setShowCancelModal(true);
  };

  // Handle confirmed cancel - calls cancel API and navigates back
  const handleConfirmCancel = async () => {
    if (cancelling || !purchase) {
      return;
    }

    setCancelling(true);
    try {
      await schedulingFetchPost(`/stripe_purchases/${purchase.id}/cancel`, {});
      setShowCancelModal(false);
      history.push(returnUrl || displayInfo?.cancelRedirectUrl || "/");
    } catch (err) {
      console.error("Error cancelling purchase:", err);
      setErrorModalMessage("Unable to cancel purchase. Please try again.");
      setShowErrorModal(true);
      setCancelling(false);
    }
  };

  const handleModalCancel = () => {
    setShowCancelModal(false);
  };

  // Handle pay/confirm - calls checkout API
  const handleConfirm = async () => {
    if (submitting || !purchase) {
      return;
    }

    // Validate credit_bundle selection
    if (selectedPaymentMethod === "credit_bundle" && !selectedBundleId) {
      if (packageRequired) {
        setErrorModalTitle(t("Package Required"));
        setErrorModalMessage(t("This session requires a package. Please purchase an offering that includes this booking option."));
      } else {
        setErrorModalTitle(undefined);
        setErrorModalMessage(t("Please select a package to use."));
      }
      setShowErrorModal(true);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Build endpoint with credit_bundle_id if needed
      let endpoint = `/stripe_purchases/${purchase.id}/checkout?type=${selectedPaymentMethod}`;
      if (selectedPaymentMethod === "credit_bundle" && selectedBundleId) {
        endpoint += `&credit_bundle_id=${selectedBundleId}`;
      }

      const body = displayInfo?.type === "lesson" && notes.trim() ? { notes: notes.trim() } : {};
      const response = await schedulingFetchPost(endpoint, body);

      if (selectedPaymentMethod === "offline" || selectedPaymentMethod === "credit_bundle") {
        // Offline/credit_bundle payment - registration completed, show appropriate modal
        if (allWaitlisted) {
          setShowWaitlistModal(true);
        } else if (displayInfo?.type === "lesson") {
          // For lessons, populate bookSession state for the success modal
          const coach = displayInfo.coaches?.[0];
          dispatch(setBookSession({
            coach: coach ? {
              fullName: [coach.firstName, coach.lastName].filter(Boolean).join(' '),
              profilePicture: coach.profilePicture,
            } : null,
            date: displayInfo.date,
            time: {
              startHour: DateTime.fromISO(displayInfo.startTime, { zone: displayInfo.timeZone }).toMillis(),
              timeZone: displayInfo.timeZone,
            },
            resourceLines: displayInfo.resourceLines ?? [],
          }));
          // Redirect to show the session booked modal
          history.push(displayInfo?.successRedirectUrl || "/sessions?session_booked_modal=true");
        } else {
          setShowSuccessModal(true);
        }
      } else {
        // Online payment - redirect to Stripe checkout
        if (response.checkoutUrl) {
          window.location.href = response.checkoutUrl;
        } else {
          setErrorModalMessage("Unable to get payment URL. Please try again.");
          setShowErrorModal(true);
          setSubmitting(false);
        }
      }
    } catch (err) {
      console.error("Error processing checkout:", err);
      const e = err as any;
      const message = e?.error || e?.errors?.[0] || "Unable to process payment. Please try again.";
      setErrorModalMessage(message);
      setShowErrorModal(true);
      setSubmitting(false);
    }
  };

  // Handle success modal close - navigate to success URL
  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    history.push(displayInfo?.successRedirectUrl || "/");
  };

  // Handle waitlist modal close - navigate to success URL
  const handleWaitlistClose = () => {
    setShowWaitlistModal(false);
    history.push(displayInfo?.successRedirectUrl || "/");
  };

  // Determine if payment method can be changed (more than one option available)
  const hasPackageOption = showPackage && groupedBundles.length > 0;
  const availableOptionsCount = [showOnline, showOffline, hasPackageOption].filter(Boolean).length;
  const canChangePaymentMethod = availableOptionsCount > 1;

  // Block confirm button until bundles API completes for session bookings
  const needsBundlesFetch = displayInfo?.type === "lesson" && !!displayInfo?.lessonRateId && !!displayInfo?.coachId;

  // Detect when no payment methods are available (e.g. online-only coach with Stripe disabled, no packages)
  const checkoutReady = !loading && !!purchase && (!needsBundlesFetch || bundlesFetched);
  const hasAnyPaymentOption = showOnline || showOffline || hasPackageOption;
  const noPaymentMethodsAvailable = checkoutReady && !hasAnyPaymentOption;
  const confirmDisabled = submitting || cancelling || !purchase || (needsBundlesFetch && !bundlesFetched) || noPaymentMethodsAvailable;

  // Auto-show modal when no payment methods are available.
  // eslint-disable-next-line react-hooks/rules-of-hooks -- false positive: every hook in this component is called unconditionally at the top; the only early returns are the loading/error guards further below.
  useEffect(() => {
    if (noPaymentMethodsAvailable) {
      setShowNoMethodsModal(true);
    }
  }, [noPaymentMethodsAvailable]);

  // Determine labels based on type
  const itemTypeLabel = displayInfo?.itemTypeLabel || (displayInfo?.type === "lesson" ? t("Session") : t("Clinic/Event"));
  const rateLabel = displayInfo?.rateLabel || (displayInfo?.type === "lesson" ? t("Session Rate") : t("Clinic/Event Rate"));
  const navbarLabel = displayInfo?.type === "lesson" ? t("Booking Summary") : t("Registration Summary");

  // Loading state
  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.scrollableContent}>
          <div className={styles.loading}>Loading...</div>
          <Loading loadType="spinner" isLoading />
        </div>
      </div>
    );
  }

  // Error state
  if (error && !purchase) {
    return (
      <div className={styles.page}>
        <div className={styles.scrollableContent}>
          <div className={styles.error}>{error}</div>
          <button className={styles.backBtn} onClick={() => history.push(returnUrl || displayInfo?.cancelRedirectUrl || "/")}>
            {t("Go Back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ maxWidth: 1018, width: "100%", margin: "0 auto" }}>
        <Navbar label={navbarLabel} hasSearch={false} hasNotifications={false} onBackClick={handleCancel}>
          <TimezoneInfo timeZone={effectiveTimeZone} />
        </Navbar>
      </div>
      <div className={styles.page}>
        <div className={styles.banner}>
          <div className={styles.bannerText}>{t(displayInfo?.bannerText) ?? t("You are about to complete a booking")}</div>
          <div className={styles.bannerTitle}>{effectiveTitle ?? "—"}</div>
          {purchase?.expiresAt && (
            <div className={styles.reservationTimer}>
              {isExpired ? (
                <span className={styles.expiredText}>{t("Reservation expired")}</span>
              ) : (
                <>
                  <span>{t("Your spot is reserved for")}</span>
                  <span className={styles.timeBadge}>{countdown}</span>
                </>
              )}
            </div>
          )}
        </div>
        <div className={styles.scrollableContent}>
          <div className={styles.specList}>
            <div className={styles.specRow}>
              <div className={styles.left}>{itemTypeLabel}</div>
              <div className={styles.value}>{effectiveTitle ?? "—"}</div>
            </div>

            <div className={styles.specRow}>
              <div className={styles.left}>{t("Coach(es)")}</div>
              <div className={styles.value}>
                <div className={styles.coaches}>
                  {effectiveCoaches?.map((c: any) => {
                    const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
                    return (
                      <div className={styles.coachItem} key={c.externalId || name}>
                        <UserAvatar
                          src={c.profilePicture}
                          height={48}
                          width={48}
                          font_size={10}
                          first_name={c.firstName}
                          last_name={c.lastName}
                        />
                        <span>{name}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className={`${styles.specRow} ${styles.separatorBottom}`}>
              <div className={styles.left}>{t("Facility")}</div>
              <div className={styles.value}>{effectiveFacilityName ?? "—"}</div>
            </div>

            <div className={styles.specRow}>
              <div className={styles.leftWithIcon}>
                <span className={styles.iconBubble}>
                  <i className="ico ico-calendar"></i>
                </span>
                <span>{t("Date")}</span>
              </div>
              <div className={styles.valueStrong}>{dateLabel}</div>
            </div>

            <div className={styles.specRow}>
              <div className={styles.leftWithIcon}>
                <span className={styles.iconBubble}>
                  <i className="ico ico-clock"></i>
                </span>
                <span>{t("Time")}</span>
              </div>
              <div className={styles.valueStrong}>{timeLabel}</div>
            </div>

            <div className={styles.specRow}>
              <div className={styles.leftWithIcon}>
                <span className={styles.iconBubble}>
                  <i className="ico ico-map-marker"></i>
                </span>
                <span>{t("Location")}</span>
              </div>
              <div className={`${styles.value} ${styles.truncateRight}`}>{effectiveLocation ?? "—"}</div>
            </div>
            <ResourceRows lines={displayInfo?.resourceLines} />

            {/* CN-2711: the booking option keeps its price to itself, so the
                summary confirms the booking without quoting one. */}
            {!displayInfo?.hidePrice && (
              <div className={`${styles.specRow} ${styles.separatorBottom}`}>
                <div className={styles.left}>{rateLabel}</div>
                <div className={styles.value}>{priceDisplay}</div>
              </div>
            )}
          </div>

          {/* CN-1323: the dependent this session is booked for. */}
          {participantDisplay && (
            <div className={styles.participantRow}>
              <UserAvatar
                src={participantDisplay.avatar || undefined}
                height={48}
                width={48}
                font_size={10}
                first_name={participantDisplay.firstName}
                last_name={participantDisplay.lastName}
              />
              <div className={styles.participantText}>
                <span className={styles.participantName}>{participantDisplay.name}</span>
                {participantDisplay.subLabel && (
                  <span className={styles.participantSub}>{participantDisplay.subLabel}</span>
                )}
              </div>
            </div>
          )}

          {/* Show registered/waitlisted students only for programs */}
          {displayInfo?.type === "program" && registrationCandidates.length > 0 && (
            <>
              <div className={styles.subSectionLabel}>{t("Registered")}</div>
              <UserItems
                separatorStyle={styles.line_separator}
                userList={registrationCandidates}
                allowRemove={false}
                showPaymentMethod={false}
                currentUserId={currentUser?.id}
              />
            </>
          )}

          {displayInfo?.type === "program" && waitlistCandidates.length > 0 && (
            <>
              <div className={styles.subSectionLabel}>{t("Waitlisted")}</div>
              <UserItems
                separatorStyle={styles.line_separator}
                userList={waitlistCandidates}
                allowRemove={false}
                showPaymentMethod={false}
                currentUserId={currentUser?.id}
                waitlisted={true}
              />
            </>
          )}

          {!displayInfo?.hidePrice && (
            <div className={`${styles.totalRow} ${styles.separatorBottom}`}>
              <div>{t("Total")}</div>
              <div className={styles.totalValue}>{totalDisplay}</div>
            </div>
          )}

          <div className={styles.section_title}>{t("Payment Method")}</div>

          {packageRequired && (
            <div className={styles.package_required_notice}>
              <i className={`ico ico-info ${styles.package_required_icon}`} />
              <div>
                <strong>{t("Package Required")}</strong>
                <p>{t("This session requires a package. Please purchase an offering that includes this booking option.")}</p>
              </div>
            </div>
          )}

          {noPaymentMethodsAvailable && !packageRequired && (
            <div className={styles.noPaymentMethods}>
              No payment methods are currently available for this booking. Please contact your coach.
            </div>
          )}

          {showPackage && groupedBundles.length > 0 && (
              <div
                className={`${styles.payment_section} ${selectedPaymentMethod === "credit_bundle" ? styles.selected : ""}`}
                onClick={() => {
                  setSelectedPaymentMethod("credit_bundle");
                  if (!selectedBundleId && groupedBundles.length > 0) {
                    setSelectedBundleId(groupedBundles[0].creditBundleIds[0]);
                  }
                }}
              >
                <div className={styles.payment_header}>
                  <div className={styles.payment_icon_text}>
                    <i className={`ico ico-dollar-circle ${styles.payment_icon}`} />
                    <div>
                      <strong>{t("Package / Subscription")}</strong>
                      <p>{t("Use sessions from your pre-purchased package.")}</p>
                    </div>
                  </div>
                  <input
                    type="radio"
                    checked={selectedPaymentMethod === "credit_bundle"}
                    readOnly
                    className={styles.radio_right}
                  />
                </div>

                {selectedPaymentMethod === "credit_bundle" && (
                  <>
                    <div className={styles.bundle_divider} />
                    <div className={styles.bundles_list}>
                      {groupedBundles.map((grouped) => {
                        const bundleId = grouped.creditBundleIds[0];
                        return (
                          <div
                            key={grouped.product.id}
                            className={`${styles.bundle_item} ${selectedBundleId === bundleId ? styles.bundle_item_selected : ""}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedBundleId(bundleId);
                            }}
                          >
                            <div
                              className={`${styles.bundle_radio} ${selectedBundleId === bundleId ? styles.bundle_radio_checked : ""}`}
                            >
                              {selectedBundleId === bundleId && (
                                <img src={CheckMark} alt="Selected" className={styles.bundle_checkmark} />
                              )}
                            </div>
                            <div className={styles.bundle_info}>
                              <div className={styles.bundle_name}>{grouped.product.title}</div>
                              <div className={styles.bundle_meta}>
                                {grouped.rateLinkGroups[0]?.title || t("Sessions")} /{" "}
                                {grouped.hasUnlimited ? "\u221E" : grouped.totalRemainingSessions} remaining
                              </div>
                              {/* CFTB-3423: several packages can cover the same
                                  session, so name the date the athlete would
                                  lose credits on \u2014 that is how they know to
                                  spend the soonest-expiring one first. Packages
                                  only: a subscription bundle's expiredAt is its
                                  renewal date, which is not an expiry at all. */}
                              {grouped.product.productType === "product" && grouped.expiredAt && (
                                <div className={styles.bundle_expiry}>
                                  {t("Expires {{date}}", {
                                    date: formatMonthDayWithYearIfNeeded(
                                      DateTime.fromISO(grouped.expiredAt),
                                    ),
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
              </div>
            )}

          {showOnline && (
            <div
              className={`${styles.payment_section} ${selectedPaymentMethod === "online" ? styles.selected : ""} ${!canChangePaymentMethod ? styles.disabled : ""}`}
              onClick={() => canChangePaymentMethod && setSelectedPaymentMethod("online")}
            >
              <div className={styles.payment_header}>
                <div className={styles.payment_icon_text}>
                  <img src={OnlinePayment} alt="Online payment" className={styles.payment_icon_image} />
                  <div>
                    <strong>{t("Online")}</strong>
                    <p>
                      <Trans
                        i18nKey="On the next step you will be asked to make a payment by credit card"
                        components={{ strong: <strong /> }}
                      />
                    </p>
                  </div>
                </div>
                <input
                  type="radio"
                  checked={selectedPaymentMethod === "online"}
                  readOnly
                  className={styles.radio_right}
                  disabled={!canChangePaymentMethod}
                />
              </div>
            </div>
          )}

          {showOffline && (
            <div
              className={`${styles.payment_section} ${selectedPaymentMethod === "offline" ? styles.selected : ""} ${!canChangePaymentMethod ? styles.disabled : ""}`}
              onClick={() => canChangePaymentMethod && setSelectedPaymentMethod("offline")}
            >
              <div className={styles.payment_header}>
                <div className={styles.payment_icon_text}>
                  <img src={OfflinePayment} alt="Offline icon" className={styles.payment_icon_image} />
                  <div>
                    <strong>{t("Offline")}</strong>
                    <p>{t("The payment will be collected manually in-person by the coach.")}</p>
                  </div>
                </div>
                <input
                  type="radio"
                  checked={selectedPaymentMethod === "offline"}
                  readOnly
                  className={styles.radio_right}
                  disabled={!canChangePaymentMethod}
                />
              </div>
            </div>
          )}

          {displayInfo?.type === "lesson" && (
            <>
              <div className={styles.section_title}>{t("Notes")}</div>
              <textarea
                className={styles.notes_input}
                placeholder={t("Add a note for your coach (optional)")}
                value={notes}
                onChange={(ev) => setNotes(ev.target.value)}
                maxLength={1000}
              />
            </>
          )}
        </div>

        <div className={styles.footerBar}>
          <button className={styles.payBtn} onClick={handleConfirm} disabled={confirmDisabled || packageRequired}>
            {submitting ? t("Processing...") : selectedPaymentMethod === "online" ? t("Pay Now") : t("Confirm")}
          </button>
          <button className={styles.cancelBtn} onClick={handleCancel} disabled={cancelling || submitting}>
            {cancelling ? t("Cancelling...") : t("Cancel")}
          </button>
          {purchase?.expiresAt && (
            <div className={styles.reservationTimer}>
              {isExpired ? (
                <span className={styles.expiredText}>{t("Reservation expired")}</span>
              ) : (
                <>
                  <span>{t("Your spot is reserved for")}</span>
                  <span className={styles.timeBadge}>{countdown}</span>
                </>
              )}
            </div>
          )}
        </div>

        <SuccessRegistrationModal isOpen={showSuccessModal} onClose={handleSuccessClose} />

        <WaitlistConfirmationModal isOpen={showWaitlistModal} onClose={handleWaitlistClose} />

        <CancelPurchaseConfirmationModal
          isOpen={showCancelModal}
          onConfirm={handleConfirmCancel}
          onCancel={handleModalCancel}
          isLoading={cancelling}
          title={displayInfo?.type === "lesson" ? "Cancel Booking" : undefined}
          message={displayInfo?.type === "lesson" ? "Please confirm that you would like to cancel your booking." : undefined}
        />

        <CheckoutErrorModal
          isOpen={showErrorModal}
          onClose={() => setShowErrorModal(false)}
          message={errorModalMessage}
          title={errorModalTitle}
        />

        <CancelPurchaseConfirmationModal
          isOpen={showNoMethodsModal}
          onConfirm={handleConfirmCancel}
          onCancel={() => setShowNoMethodsModal(false)}
          isLoading={cancelling}
          title={displayInfo?.type === "lesson" ? "Unable to Complete Booking" : "Unable to Complete Registration"}
          message="There are no payment methods available. The booking will be cancelled."
          hideCancel
          confirmLabel="Ok"
        />
      </div>
    </>
  );
}
